import { renderRcdIcons } from "../core/icons";
import * as rcdCalendar from "../core/calendar";

const calendarLabels = (state) => {
    const locale = document.documentElement.lang || undefined;
    const calendar = state.calendarSystem === "Persian" ? "persian" : "gregory";
    const monthDate = state.calendarSystem === "Persian"
        ? rcdCalendar.toGregorianDate(state.year, state.month, 1)
        : new Date(state.year, state.month - 1, 1);
    const monthName = new Intl.DateTimeFormat(locale, { calendar, month: "long" }).format(monthDate);
    const weekDays = Array.from({ length: 7 }, (_, index) =>
        new Intl.DateTimeFormat(locale, { weekday: "short" }).format(new Date(2024, 0, 7 + index))
    );
    return { monthName, weekDays };
};

const positionCalendarPopover = (input, popover) => {
    const field = input.closest(".rcd-calendar-field");
    const shell = input.closest(".rcd-shell");
    const rect = (field || input).getBoundingClientRect();
    const viewport = window.visualViewport || { width: window.innerWidth, height: window.innerHeight };
    const margin = 12;
    const width = Math.min(336, Math.max(280, viewport.width - (margin * 2)));
    const popoverHeight = popover.offsetHeight || 360;
    const hasRoomBelow = rect.bottom + margin + popoverHeight <= viewport.height;
    const top = hasRoomBelow
        ? rect.bottom + margin
        : Math.max(margin, rect.top - margin - popoverHeight);
    const isRtl = document.documentElement.dir === "rtl" || shell?.classList.contains("rtl");
    const preferredLeft = isRtl ? rect.right - width : rect.left;
    const left = Math.min(Math.max(margin, preferredLeft), viewport.width - width - margin);

    popover.style.width = `${width}px`;
    popover.style.top = `${Math.round(top)}px`;
    popover.style.left = `${Math.round(left)}px`;
    popover.classList.toggle("is-light", shell?.dataset.theme === "light");
};

const renderCalendarPicker = (input, popover, state) => {
    const messages = window.rcdMessages || {};
    const { monthName, weekDays } = calendarLabels(state);
    const firstWeekday = rcdCalendar.weekday(state.year, state.month, 1, state.calendarSystem);
    const daysInMonth = rcdCalendar.daysInMonth(state.year, state.month, state.calendarSystem);
    const cells = [];

    for (let i = 0; i < firstWeekday; i += 1) {
        cells.push('<span class="rcd-calendar-day is-empty"></span>');
    }

    for (let day = 1; day <= daysInMonth; day += 1) {
        cells.push(`<button type="button" class="rcd-calendar-day${day === state.day ? " is-selected" : ""}" data-day="${day}">${day}</button>`);
    }

    popover.innerHTML = `
        <div class="rcd-calendar-head">
            <button type="button" class="rcd-icon-button" data-calendar-prev aria-label="${messages.previousMonth}"><i data-lucide="chevron-left"></i></button>
            <strong>${monthName} ${state.year}</strong>
            <button type="button" class="rcd-icon-button" data-calendar-next aria-label="${messages.nextMonth}"><i data-lucide="chevron-right"></i></button>
        </div>
        <div class="rcd-calendar-weekdays">${weekDays.map((day) => `<span>${day}</span>`).join("")}</div>
        <div class="rcd-calendar-grid">${cells.join("")}</div>
        ${state.mode === "datetime" ? `
            <div class="rcd-calendar-time">
                <input type="number" min="0" max="23" value="${rcdCalendar.padPart(state.hour)}" data-calendar-hour aria-label="${messages.hour}" />
                <span>:</span>
                <input type="number" min="0" max="59" value="${rcdCalendar.padPart(state.minute)}" data-calendar-minute aria-label="${messages.minute}" />
            </div>
        ` : ""}
        <div class="rcd-calendar-actions">
            <button type="button" class="rcd-button rcd-button--ghost" data-calendar-today>${messages.today}</button>
            <button type="button" class="rcd-button rcd-button--primary" data-calendar-apply>${messages.apply}</button>
        </div>
    `;

    renderRcdIcons();

    positionCalendarPopover(input, popover);

    popover.querySelector("[data-calendar-prev]")?.addEventListener("click", (event) => {
        event.preventDefault();
        event.stopPropagation();
        state.month -= 1;
        if (state.month < 1) {
            state.month = 12;
            state.year -= 1;
        }
        state.day = Math.min(state.day, rcdCalendar.daysInMonth(state.year, state.month, state.calendarSystem));
        renderCalendarPicker(input, popover, state);
    });

    popover.querySelector("[data-calendar-next]")?.addEventListener("click", (event) => {
        event.preventDefault();
        event.stopPropagation();
        state.month += 1;
        if (state.month > 12) {
            state.month = 1;
            state.year += 1;
        }
        state.day = Math.min(state.day, rcdCalendar.daysInMonth(state.year, state.month, state.calendarSystem));
        renderCalendarPicker(input, popover, state);
    });

    popover.querySelectorAll("[data-day]").forEach((button) => {
        button.addEventListener("click", (event) => {
            event.preventDefault();
            event.stopPropagation();
            state.day = Number(button.dataset.day);
            input.value = rcdCalendar.formatValue(state);
            renderCalendarPicker(input, popover, state);
        });
    });

    popover.querySelector("[data-calendar-hour]")?.addEventListener("input", (event) => {
        event.stopPropagation();
        state.hour = Math.min(23, Math.max(0, Number(event.target.value || 0)));
        input.value = rcdCalendar.formatValue(state);
    });

    popover.querySelector("[data-calendar-minute]")?.addEventListener("input", (event) => {
        event.stopPropagation();
        state.minute = Math.min(59, Math.max(0, Number(event.target.value || 0)));
        input.value = rcdCalendar.formatValue(state);
    });

    popover.querySelector("[data-calendar-today]")?.addEventListener("click", (event) => {
        event.preventDefault();
        event.stopPropagation();
        Object.assign(state, rcdCalendar.stateFromDate(new Date(), state.calendarSystem, state.mode));
        input.value = rcdCalendar.formatValue(state);
        renderCalendarPicker(input, popover, state);
    });

    popover.querySelector("[data-calendar-apply]")?.addEventListener("click", (event) => {
        event.preventDefault();
        event.stopPropagation();
        input.value = rcdCalendar.formatValue(state);
        popover.hidden = true;
    });
};

const setupCalendarPickers = () => {
    document.querySelectorAll("[data-rcd-calendar-input]").forEach((input) => {
        if (input.dataset.calendarReady === "true") {
            return;
        }

        input.dataset.calendarReady = "true";
        const calendarSystem = input.dataset.calendarSystem === "Persian" ? "Persian" : "Gregorian";
        const mode = input.dataset.calendarMode === "date" ? "date" : "datetime";
        const field = document.createElement("div");
        field.className = "rcd-calendar-field";
        input.parentNode.insertBefore(field, input);
        field.appendChild(input);

        const trigger = document.createElement("button");
        trigger.type = "button";
        trigger.className = "rcd-calendar-trigger";
        trigger.setAttribute("aria-label", "Open calendar");
        trigger.innerHTML = '<i data-lucide="calendar-days"></i>';
        field.appendChild(trigger);

        const popover = document.createElement("div");
        popover.className = "rcd-calendar-popover";
        popover.hidden = true;
        document.body.appendChild(popover);

        const open = () => {
            document.querySelectorAll(".rcd-calendar-popover").forEach((otherPopover) => {
                if (otherPopover !== popover) {
                    otherPopover.hidden = true;
                }
            });

            const parsed = rcdCalendar.parseValue(input.value, calendarSystem) || new Date();
            const state = rcdCalendar.stateFromDate(parsed, calendarSystem, mode);
            renderCalendarPicker(input, popover, state);
            popover.hidden = false;
            positionCalendarPopover(input, popover);
        };

        trigger.addEventListener("click", (event) => {
            event.preventDefault();
            event.stopPropagation();
            open();
        });
        input.addEventListener("focus", open);
        input.addEventListener("click", (event) => {
            event.stopPropagation();
        });
        input.addEventListener("keydown", (event) => {
            if (event.key === "Escape") {
                popover.hidden = true;
            }
        });

        const reposition = () => {
            if (!popover.hidden) {
                positionCalendarPopover(input, popover);
            }
        };

        window.addEventListener("resize", reposition, { passive: true });
        window.addEventListener("scroll", reposition, { passive: true });

        document.addEventListener("click", (event) => {
            if (!field.contains(event.target) && !popover.contains(event.target)) {
                popover.hidden = true;
            }
        });
    });
};

export { setupCalendarPickers };
